import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const Suppliers = () => {
  // State for suppliers list
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  
  // State for master data
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  
  // State for form data
  const [formData, setFormData] = useState({
    supplierid: null,
    suppliercode: '',
    suppliername: '',
    contactperson: '',
    phone: '',
    email: '',
    mobile: '',
    website: '',
    address: '',
    cityid: '',
    stateid: '',
    countryid: '',
    pincode: '',
    gstin: '',
    pan: '',
    tan: '',
    taxrate: 0,
    creditdays: 0,
    creditlimit: 0,
    paymentterms: '',
    bankname: '',
    accountnumber: '',
    ifsccode: '',
    branch: '',
    suppliertype: '',
    category: '',
    remarks: ''
  });

  // State for UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Load data on component mount
  useEffect(() => {
    loadSuppliers();
    loadMasterData();
  }, []);

  // Filter suppliers when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSuppliers(suppliers);
    } else {
      const filtered = suppliers.filter(supplier =>
        supplier.suppliercode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.suppliername.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (supplier.phone && supplier.phone.includes(searchTerm)) ||
        (supplier.email && supplier.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (supplier.gstin && supplier.gstin.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredSuppliers(filtered);
    }
  }, [searchTerm, suppliers]);

  // Load suppliers
  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/Suppliers.php');
      if (response.data.success) {
        setSuppliers(response.data.data.suppliers || []);
        setFilteredSuppliers(response.data.data.suppliers || []);
      }
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  // Load master data
  const loadMasterData = async () => {
    try {
      // Load cities
      const citiesResponse = await apiClient.get('/Cities.php');
      if (citiesResponse.data.success) {
        setCities(citiesResponse.data.data.cities || []);
      }

      // Load states
      const statesResponse = await apiClient.get('/States.php');
      if (statesResponse.data.success) {
        setStates(statesResponse.data.data.states || []);
      }

      // Load countries
      const countriesResponse = await apiClient.get('/Countries.php');
      if (countriesResponse.data.success) {
        setCountries(countriesResponse.data.data.countries || []);
      }
    } catch (err) {
      console.error('Error loading master data:', err);
    }
  };

  // Generate supplier code
  const generateSupplierCode = async () => {
    try {
      const response = await apiClient.get('/Suppliers.php?generate_code');
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          suppliercode: response.data.data.suppliercode
        }));
      }
    } catch (err) {
      console.error('Error generating supplier code:', err);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle number input change
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? 0 : parseFloat(value)
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.suppliercode.trim()) {
      setError('Supplier code is required');
      return false;
    }
    if (!formData.suppliername.trim()) {
      setError('Supplier name is required');
      return false;
    }
    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Invalid email format');
      return false;
    }
    // GSTIN validation (if provided)
    if (formData.gstin && formData.gstin.length > 0 && formData.gstin.length !== 15) {
      setError('GSTIN must be 15 characters');
      return false;
    }
    // PAN validation (if provided)
    if (formData.pan && formData.pan.length > 0 && formData.pan.length !== 10) {
      setError('PAN must be 10 characters');
      return false;
    }
    return true;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      let response;
      if (editingId) {
        // Update existing supplier
        response = await apiClient.put('/Suppliers.php', formData);
      } else {
        // Create new supplier
        response = await apiClient.post('/Suppliers.php', formData);
      }

      if (response.data.success) {
        await loadSuppliers();
        handleClear();
        setShowForm(false);
      } else {
        setError(response.data.message || 'Failed to save supplier');
      }
    } catch (err) {
      console.error('Error saving supplier:', err);
      setError('Failed to save supplier: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (supplier) => {
    setFormData({
      supplierid: supplier.supplierid,
      suppliercode: supplier.suppliercode || '',
      suppliername: supplier.suppliername || '',
      contactperson: supplier.contactperson || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      mobile: supplier.mobile || '',
      website: supplier.website || '',
      address: supplier.address || '',
      cityid: supplier.cityid || '',
      stateid: supplier.stateid || '',
      countryid: supplier.countryid || '',
      pincode: supplier.pincode || '',
      gstin: supplier.gstin || '',
      pan: supplier.pan || '',
      tan: supplier.tan || '',
      taxrate: parseFloat(supplier.taxrate) || 0,
      creditdays: parseInt(supplier.creditdays) || 0,
      creditlimit: parseFloat(supplier.creditlimit) || 0,
      paymentterms: supplier.paymentterms || '',
      bankname: supplier.bankname || '',
      accountnumber: supplier.accountnumber || '',
      ifsccode: supplier.ifsccode || '',
      branch: supplier.branch || '',
      suppliertype: supplier.suppliertype || '',
      category: supplier.category || '',
      remarks: supplier.remarks || ''
    });
    setEditingId(supplier.supplierid);
    setShowForm(true);
    setError('');
  };

  // Handle delete
  const handleDelete = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.delete('/Suppliers.php', {
        data: { supplierid: supplierId }
      });

      if (response.data.success) {
        await loadSuppliers();
      } else {
        setError(response.data.message || 'Failed to delete supplier');
      }
    } catch (err) {
      console.error('Error deleting supplier:', err);
      setError('Failed to delete supplier: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle clear form
  const handleClear = () => {
    setFormData({
      supplierid: null,
      suppliercode: '',
      suppliername: '',
      contactperson: '',
      phone: '',
      email: '',
      mobile: '',
      website: '',
      address: '',
      cityid: '',
      stateid: '',
      countryid: '',
      pincode: '',
      gstin: '',
      pan: '',
      tan: '',
      taxrate: 0,
      creditdays: 0,
      creditlimit: 0,
      paymentterms: '',
      bankname: '',
      accountnumber: '',
      ifsccode: '',
      branch: '',
      suppliertype: '',
      category: '',
      remarks: ''
    });
    setEditingId(null);
    setError('');
  };

  // Handle new
  const handleNew = async () => {
    handleClear();
    await generateSupplierCode();
    setShowForm(true);
  };

  // Handle cancel
  const handleCancel = () => {
    handleClear();
    setShowForm(false);
  };

  return (
    <div className="master-form-container">
      <div className="master-form-header">
        <h2>🏢 Supplier Management</h2>
        <div className="header-actions">
          {!showForm && (
            <button className="btn btn-primary" onClick={handleNew}>
              ➕ New Supplier
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button className="error-dismiss" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Entry Form */}
      {showForm && (
        <div className="form-section">
          <div className="section-header">
            <h3>{editingId ? '✏️ Edit Supplier' : '➕ New Supplier'}</h3>
          </div>

          {/* Basic Information */}
          <div className="form-group-section">
            <h4 className="section-title">📋 Basic Information</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>Supplier Code <span className="required">*</span></label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    name="suppliercode"
                    value={formData.suppliercode}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="SUP0001"
                    style={{ flex: 1 }}
                  />
                  {!editingId && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={generateSupplierCode}
                      disabled={loading}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      🔄 Generate
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label>Supplier Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="suppliername"
                  value={formData.suppliername}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Supplier Name"
                />
              </div>

              <div className="form-group">
                <label>Supplier Type</label>
                <select
                  name="suppliertype"
                  value={formData.suppliertype}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- Select Type --</option>
                  <option value="VENDOR">Vendor</option>
                  <option value="CONTRACTOR">Contractor</option>
                  <option value="SERVICE_PROVIDER">Service Provider</option>
                  <option value="FREIGHT_FORWARDER">Freight Forwarder</option>
                  <option value="TRANSPORT_COMPANY">Transport Company</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Category"
                />
              </div>

              <div className="form-group">
                <label>Contact Person</label>
                <input
                  type="text"
                  name="contactperson"
                  value={formData.contactperson}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Contact Person Name"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-group-section">
            <h4 className="section-title">📞 Contact Information</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="+91-XXXXXXXXXX"
                />
              </div>

              <div className="form-group">
                <label>Mobile</label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="+91-XXXXXXXXXX"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="supplier@example.com"
                />
              </div>

              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="https://www.example.com"
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="form-group-section">
            <h4 className="section-title">📍 Address Information</h4>
            
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={loading}
                  rows="2"
                  placeholder="Street Address"
                />
              </div>

              <div className="form-group">
                <label>Pin Code</label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="123456"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <select
                  name="cityid"
                  value={formData.cityid}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- Select City --</option>
                  {cities.map(city => (
                    <option key={city.cityid} value={city.cityid}>
                      {city.cityname}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>State</label>
                <select
                  name="stateid"
                  value={formData.stateid}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- Select State --</option>
                  {states.map(state => (
                    <option key={state.stateid} value={state.stateid}>
                      {state.statename}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Country</label>
                <select
                  name="countryid"
                  value={formData.countryid}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- Select Country --</option>
                  {countries.map(country => (
                    <option key={country.countryid} value={country.countryid}>
                      {country.countryname}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Tax & Legal Information */}
          <div className="form-group-section">
            <h4 className="section-title">📄 Tax & Legal Information</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>GSTIN</label>
                <input
                  type="text"
                  name="gstin"
                  value={formData.gstin}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="15 characters"
                  maxLength="15"
                />
              </div>

              <div className="form-group">
                <label>PAN</label>
                <input
                  type="text"
                  name="pan"
                  value={formData.pan}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="10 characters"
                  maxLength="10"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="form-group">
                <label>TAN</label>
                <input
                  type="text"
                  name="tan"
                  value={formData.tan}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="TAN Number"
                  maxLength="10"
                />
              </div>

              <div className="form-group">
                <label>Tax Rate (%)</label>
                <input
                  type="number"
                  name="taxrate"
                  value={formData.taxrate}
                  onChange={handleNumberChange}
                  disabled={loading}
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="form-group-section">
            <h4 className="section-title">💳 Payment Terms</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>Credit Days</label>
                <input
                  type="number"
                  name="creditdays"
                  value={formData.creditdays}
                  onChange={handleNumberChange}
                  disabled={loading}
                  min="0"
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Credit Limit</label>
                <input
                  type="number"
                  name="creditlimit"
                  value={formData.creditlimit}
                  onChange={handleNumberChange}
                  disabled={loading}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                />
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label>Payment Terms</label>
                <input
                  type="text"
                  name="paymentterms"
                  value={formData.paymentterms}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Payment terms details"
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="form-group-section">
            <h4 className="section-title">🏦 Bank Details</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>Bank Name</label>
                <input
                  type="text"
                  name="bankname"
                  value={formData.bankname}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Bank Name"
                />
              </div>

              <div className="form-group">
                <label>Account Number</label>
                <input
                  type="text"
                  name="accountnumber"
                  value={formData.accountnumber}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Account Number"
                />
              </div>

              <div className="form-group">
                <label>IFSC Code</label>
                <input
                  type="text"
                  name="ifsccode"
                  value={formData.ifsccode}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="IFSC Code"
                  maxLength="11"
                />
              </div>

              <div className="form-group">
                <label>Branch</label>
                <input
                  type="text"
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Branch Name"
                />
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div className="form-group-section">
            <h4 className="section-title">📝 Remarks</h4>
            
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  disabled={loading}
                  rows="3"
                  placeholder="Additional notes or remarks"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? '⏳ Saving...' : editingId ? '💾 Update' : '💾 Save'}
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={handleCancel}
              disabled={loading}
            >
              ❌ Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search and Grid Section */}
      {!showForm && (
        <div className="grid-section">
          <div className="section-header">
            <h3>📋 Suppliers List ({filteredSuppliers.length})</h3>
            <div className="search-box">
              <input
                type="text"
                placeholder="🔍 Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Supplier Name</th>
                  <th>Contact Person</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>City</th>
                  <th>GSTIN</th>
                  <th>Credit Limit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && filteredSuppliers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">Loading...</td>
                  </tr>
                ) : filteredSuppliers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      {searchTerm ? 'No suppliers found matching your search.' : 'No suppliers found. Click "New Supplier" to create one.'}
                    </td>
                  </tr>
                ) : (
                  filteredSuppliers.map(supplier => (
                    <tr key={supplier.supplierid}>
                      <td>{supplier.suppliercode}</td>
                      <td><strong>{supplier.suppliername}</strong></td>
                      <td>{supplier.contactperson || '-'}</td>
                      <td>{supplier.phone || '-'}</td>
                      <td>{supplier.email || '-'}</td>
                      <td>{supplier.cityname || '-'}</td>
                      <td>{supplier.gstin || '-'}</td>
                      <td className="text-right">
                        {parseFloat(supplier.creditlimit || 0).toFixed(2)}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleEdit(supplier)}
                            title="Edit"
                            disabled={loading}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(supplier.supplierid)}
                            title="Delete"
                            disabled={loading}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;